import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration, replayIntegration } from '@sentry/angular';
import { environment } from './environments/environment';

// ─── Sentry: initialize before Angular bootstrap ──────────────────────────────
// Guard with sentryDsn so dev (empty DSN) never sends events.
if (environment.sentryDsn) {
  // Detect low-end devices: ≤ 2 CPU cores OR ≤ 1 GB RAM.
  // On these devices we skip Session Replay entirely — it continuously
  // records the DOM and sends "envelope" batches which cause visible lag.
  const cpuCores: number = (navigator as any).hardwareConcurrency ?? 4;
  const deviceRam: number = (navigator as any).deviceMemory ?? 4;
  const isLowEndDevice = cpuCores <= 2 || deviceRam <= 1;

  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.sentryEnvironment,
    release: `crm-v2@${environment.version}`,

    integrations: isLowEndDevice
      ? [
          // Only lightweight tracing on low-end devices; no replay recording.
          browserTracingIntegration(),
        ]
      : [
          browserTracingIntegration(),
          replayIntegration({
            // Mask all form/input values to protect PII.
            // Text labels and layout remain visible for debugging.
            maskAllInputs: true,
            blockAllMedia: false,
          }),
        ],

    // Skip performance tracing on low-end devices to remove XHR monkey-patching overhead.
    tracesSampleRate: isLowEndDevice ? 0 : 0.1,

    // Propagate trace headers only to our own API origins.
    tracePropagationTargets: [
      /api-manage\.aqsatel\.com/,
      /identity\.aqsatel\.com/,
      /api\.aqsatel\.com/,
    ],

    // Replay rates: zero on low-end devices; normal rates elsewhere.
    replaysOnErrorSampleRate: isLowEndDevice ? 0 : 1.0,
    replaysSessionSampleRate: isLowEndDevice ? 0 : 0.1,

    beforeSend(event, hint) {
      const err = hint?.originalException as any;
      if (!err) return event;

      // Drop ChunkLoadError — Angular's withNavigationErrorHandler already reloads the page.
      if (err?.name === 'ChunkLoadError' || String(err?.message).includes('Loading chunk')) {
        return null;
      }

      // Drop 401 — handled by ApiUnauthorisedInterceptor (token refresh / logout flow).
      if (err?.status === 401) return null;

      // Drop status 0 — request was cancelled (user navigated away or network blip).
      if (err?.status === 0) return null;

      // Drop browser observer noise.
      if (String(err?.message).includes('ResizeObserver loop limit exceeded')) {
        return null;
      }

      // Drop IndexedDB NotFoundError — these are schema-migration artefacts,
      // not actionable bugs, and on low-end devices they fire repeatedly.
      if (err?.name === 'NotFoundError' && String(err?.message).includes('IDBDatabase')) {
        return null;
      }

      return event;
    },
  });
}
// ─────────────────────────────────────────────────────────────────────────────

// Suppress Angular trackBy duplicate keys warnings
const originalWarn = console.warn;
console.warn = function(...args: any[]) {
  const message = args[0];
  if (typeof message === 'string' &&
      (message.includes('NG0955') ||
       message.includes('duplicated keys') ||
       message.includes('track expression'))) {
    return; // Suppress this specific warning
  }
  originalWarn.apply(console, args);
};

// Hide initial loader once app is ready
function hideInitialLoader() {
  const loader = document.getElementById('initial-loader');
  if (loader) {
    loader.style.display = 'none';
  }
  document.body.classList.add('app-loaded');
}

bootstrapApplication(AppComponent, appConfig)
  .then(() => {
    hideInitialLoader();
    console.log('🚀 TIBSS Next application started successfully');
  })
  .catch((err) => {
    console.error('❌ Error starting TIBSS Next application:', err);
    hideInitialLoader();
  });
